import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
} from "@mui/material";
import toast from "react-hot-toast";
import configService from "../services/config";

const Complaints = () => {
  const { company_id, userId } = useSelector((state) => state.globalVariables);

  const [myComplaints, setMyComplaints] = useState([]);
  const [openComplaints, setOpenComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [startingId, setStartingId] = useState(null);

  const fetchComplaints = async () => {
    if (!userId) return;
    try {
      setComplaintsLoading(true);

      const [mineRes, openRes] = await Promise.all([
        configService.complaintMyComplaints({ staff_id: userId }),
        configService.complaintPendingByCategory({ company_id, staff_id: userId }),
      ]);

      if (mineRes.status) setMyComplaints(mineRes.data || []);
      if (openRes.status) setOpenComplaints(openRes.data || []);
    } catch (err) {
      console.error("Complaints fetch error:", err);
      toast.error("Failed to load complaints.");
    } finally {
      setComplaintsLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints();
  }, [userId]);

  const handleStartWork = async (complaintId) => {
    try {
      setStartingId(complaintId);
      const res = await configService.complaintStartWork({
        complaint_id: complaintId,
        staff_id: userId,
      });

      if (res.status) {
        toast.success(res.message || "Work started");
        fetchComplaints();
      } else {
        toast.error(res.message || "Failed to start work.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to start work.");
    } finally {
      setStartingId(null);
    }
  };

  const getComplaintStatusColor = (status) => {
    const map = {
      pending: "#f39c12",
      approved: "#42a5f5",
      in_progress: "#1976d2",
      closed: "#2ecc71",
      denied: "#e74c3c",
    };
    return map[status] ?? "#42a5f5";
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f4f6f9", minHeight: "100vh" }}>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* My Complaints card                                          */}
      {/* ══════════════════════════════════════════════════════════ */}
      <Card sx={{ bgcolor: "#ffffff", borderRadius: 4, boxShadow: "0 10px 25px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#1e293b" }}>
            My Complaints
          </Typography>

          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Complaint ID</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Category</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Priority</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complaintsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", color: "#64748b" }}>Loading...</TableCell>
                </TableRow>
              ) : myComplaints.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", color: "#64748b" }}>No complaints assigned to you.</TableCell>
                </TableRow>
              ) : myComplaints.map((c) => (
                <TableRow key={c.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{c.complaint_id || c.id}</TableCell>
                  <TableCell>{c.category?.name || "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={c.priority} />
                  </TableCell>
                  <TableCell sx={{ color: getComplaintStatusColor(c.status), fontWeight: "bold" }}>
                    {c.status}
                  </TableCell>
                  <TableCell>
                    {c.status === "approved" && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={startingId === c.id}
                        onClick={() => handleStartWork(c.id)}
                      >
                        {startingId === c.id ? "Starting..." : "Start Work"}
                      </Button>
                    )}
                    {c.status === "in_progress" && (
                      <Typography sx={{ color: "#1976d2", fontWeight: "bold", fontSize: 13 }}>In Progress</Typography>
                    )}
                    {c.status === "closed" && (
                      <Typography sx={{ color: "#2e7d32", fontWeight: "bold", fontSize: 13 }}>Closed</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* Open Complaints (My Categories) card                        */}
      {/* ══════════════════════════════════════════════════════════ */}
      <Card sx={{ mt: 3, bgcolor: "#ffffff", borderRadius: 4, boxShadow: "0 10px 25px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#1e293b" }}>
            Open Complaints (My Categories)
          </Typography>

          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Complaint ID</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Category</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Priority</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ color: "#475569", fontWeight: "bold" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complaintsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", color: "#64748b" }}>Loading...</TableCell>
                </TableRow>
              ) : openComplaints.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", color: "#64748b" }}>No open complaints right now.</TableCell>
                </TableRow>
              ) : openComplaints.map((c) => (
                <TableRow key={c.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{c.complaint_id || c.id}</TableCell>
                  <TableCell>{c.category?.name || "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={c.priority} />
                  </TableCell>
                  <TableCell sx={{ color: getComplaintStatusColor(c.status), fontWeight: "bold" }}>
                    {c.status}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={startingId === c.id}
                      onClick={() => handleStartWork(c.id)}
                    >
                      {startingId === c.id ? "Starting..." : "Start Work"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};

export default Complaints;